import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-sql';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { BarChart3, Download, Table2 } from 'lucide-react';

const CHART_COLORS = [
  '#ff6738',
  '#f47835',
  '#fecda5',
  '#22c55e',
  '#eab308',
  '#14b8a6',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#6366f1',
];

interface ChartData {
  type: 'bar' | 'pie';
  data: Record<string, string | number>[];
  labelKey: string;
  valueKey: string;
  title?: string;
}

function parseChartBlock(code: string): ChartData | null {
  try {
    const parsed = JSON.parse(code);
    if (parsed.data && Array.isArray(parsed.data) && parsed.labelKey && parsed.valueKey) {
      return parsed as ChartData;
    }
  } catch {
    // not valid chart JSON
  }
  return null;
}

/**
 * Try to detect a markdown table that could be charted.
 * Returns data array if the table has exactly 2 columns where the second is numeric.
 */
function tryTableToChart(rows: Record<string, string | number>[], headers: string[]): ChartData | null {
  if (headers.length < 2 || rows.length < 2 || rows.length > 30) return null;

  // Check if the second column is mostly numeric
  const numericCount = rows.filter((r) => {
    const val = r[headers[1]];
    return typeof val === 'number' || (typeof val === 'string' && !isNaN(Number(val.replace(/,/g, ''))));
  }).length;

  if (numericCount / rows.length < 0.8) return null;

  const data = rows.map((r) => ({
    ...r,
    [headers[1]]: typeof r[headers[1]] === 'string' ? Number(String(r[headers[1]]).replace(/,/g, '')) : r[headers[1]],
  }));

  return {
    type: rows.length <= 8 ? 'pie' : 'bar',
    data,
    labelKey: headers[0],
    valueKey: headers[1],
  };
}

function ChartRenderer({ chart }: { chart: ChartData }) {
  if (chart.type === 'pie' && chart.data.length <= 8) {
    return (
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={chart.data}
            dataKey={chart.valueKey}
            nameKey={chart.labelKey}
            cx="50%"
            cy="50%"
            outerRadius={100}
            label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
            labelLine={false}
          >
            {chart.data.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(20 15% 12%)',
              border: '1px solid hsl(14 100% 61% / 0.3)',
              borderRadius: '8px',
              color: 'hsl(210 20% 98%)',
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chart.data} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(14 100% 61% / 0.1)" />
        <XAxis
          dataKey={chart.labelKey}
          tick={{ fill: 'hsl(210 20% 70%)', fontSize: 11 }}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis tick={{ fill: 'hsl(210 20% 70%)', fontSize: 11 }} />
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(20 15% 12%)',
            border: '1px solid hsl(14 100% 61% / 0.3)',
            borderRadius: '8px',
            color: 'hsl(210 20% 98%)',
          }}
        />
        <Bar dataKey={chart.valueKey} fill="#ff6738" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function downloadCsv(headers: string[], rows: Record<string, string | number>[]) {
  const escapeCsv = (val: string | number) => {
    const s = String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const lines = [
    headers.map(escapeCsv).join(','),
    ...rows.map((row) => headers.map((h) => escapeCsv(row[h] ?? '')).join(',')),
  ];

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `data-export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function TableWithChart({
  children,
  headers,
  rows,
}: {
  children: React.ReactNode;
  headers: string[];
  rows: Record<string, string | number>[];
}) {
  const [showChart, setShowChart] = useState(false);
  const chart = useMemo(() => tryTableToChart(rows, headers), [rows, headers]);
  const hasData = headers.length > 0 && rows.length > 0;

  return (
    <div>
      {(chart || hasData) && (
        <div className="flex justify-end gap-1.5 mb-2">
          {hasData && (
            <button
              onClick={() => downloadCsv(headers, rows)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors border border-primary/20"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
          )}
          {chart && (
            <button
              onClick={() => setShowChart(!showChart)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors border border-primary/20"
            >
              {showChart ? (
                <>
                  <Table2 className="h-3.5 w-3.5" /> Table
                </>
              ) : (
                <>
                  <BarChart3 className="h-3.5 w-3.5" /> Chart
                </>
              )}
            </button>
          )}
        </div>
      )}
      {showChart && chart ? (
        <div className="bg-background/50 rounded-lg border border-primary/20 p-4">
          <ChartRenderer chart={chart} />
        </div>
      ) : (
        children
      )}
    </div>
  );
}

export default function ChatMarkdown({ content, isUser }: { content: string; isUser?: boolean }) {
  // Track table data for chart conversion (mutated by th/td render callbacks)
  const currentHeaders: string[] = [];
  const currentRows: Record<string, string | number>[] = [];

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Headings
        h1: ({ children }: { children?: React.ReactNode }) => <h1 className="text-lg font-bold mt-4 mb-2 text-foreground">{children}</h1>,
        h2: ({ children }: { children?: React.ReactNode }) => <h2 className="text-base font-bold mt-3 mb-1.5 text-foreground">{children}</h2>,
        h3: ({ children }: { children?: React.ReactNode }) => <h3 className="text-sm font-bold mt-2 mb-1 text-foreground">{children}</h3>,

        // Paragraphs
        p: ({ children }: { children?: React.ReactNode }) => (
          <p className={`mb-2 last:mb-0 leading-relaxed ${isUser ? 'text-white' : 'text-foreground'}`}>{children}</p>
        ),

        // Bold / italic
        strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-bold">{children}</strong>,
        em: ({ children }: { children?: React.ReactNode }) => <em className="italic">{children}</em>,

        // Lists
        ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc list-inside mb-2 space-y-0.5">{children}</ul>,
        ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal list-inside mb-2 space-y-0.5">{children}</ol>,
        li: ({ children }: { children?: React.ReactNode }) => (
          <li className={`${isUser ? 'text-white/90' : 'text-foreground/90'} text-sm`}>{children}</li>
        ),

        // Links
        a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 underline underline-offset-2"
          >
            {children}
          </a>
        ),

        // Code blocks
        code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
          const match = /language-(\w+)/.exec(className || '');
          const lang = match ? match[1] : null;
          const code = String(children).replace(/\n$/, '');

          // Chart code block
          if (lang === 'chart') {
            const chart = parseChartBlock(code);
            if (chart) {
              return (
                <div className="my-3 bg-background/50 rounded-lg border border-primary/20 p-4">
                  {chart.title && <div className="text-sm font-medium text-foreground mb-3">{chart.title}</div>}
                  <ChartRenderer chart={chart} />
                </div>
              );
            }
          }

          // SQL code block
          if (lang === 'sql') {
            return (
              <div className="my-3 border border-primary/30 rounded-lg overflow-hidden">
                <div className="bg-primary/10 px-3 py-1.5 border-b border-primary/30">
                  <span className="text-xs font-semibold text-foreground">SQL</span>
                </div>
                <pre className="bg-[#1e1e1e] p-3 overflow-x-auto">
                  <code
                    className="text-[13px] font-mono leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: highlight(code, languages.sql, 'sql'),
                    }}
                  />
                </pre>
              </div>
            );
          }

          // Other code blocks
          if (lang || code.includes('\n')) {
            return (
              <pre className="my-3 bg-background/80 border border-primary/20 rounded-lg p-3 overflow-x-auto">
                <code className="text-sm font-mono text-foreground/90">{code}</code>
              </pre>
            );
          }

          // Inline code
          return <code className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-sm font-mono">{children}</code>;
        },

        // Tables
        table: ({ children }: { children?: React.ReactNode }) => {
          // Wrap in TableWithChart for auto-chart detection
          const tableEl = (
            <div className="my-3 overflow-x-auto">
              <table className="w-full text-sm border-collapse">{children}</table>
            </div>
          );

          return (
            <TableWithChart headers={currentHeaders} rows={currentRows}>
              {tableEl}
            </TableWithChart>
          );
        },
        thead: ({ children }: { children?: React.ReactNode }) => <thead className="bg-primary/10 border-b border-primary/30">{children}</thead>,
        tbody: ({ children }: { children?: React.ReactNode }) => <tbody className="divide-y divide-primary/10">{children}</tbody>,
        tr: ({ children }: { children?: React.ReactNode }) => <tr className="hover:bg-primary/5 transition-colors">{children}</tr>,
        th: ({ children }: { children?: React.ReactNode }) => {
          const text = String(children || '');
          currentHeaders.push(text);
          return (
            <th className="px-3 py-2 text-left text-xs font-bold text-foreground uppercase tracking-wider">
              {children}
            </th>
          );
        },
        td: ({ children }: { children?: React.ReactNode }) => {
          // Accumulate row data for chart detection
          const text = String(children || '');
          const headerIndex = currentRows.length > 0 ? Object.keys(currentRows[currentRows.length - 1]).length : 0;

          if (headerIndex === 0) {
            currentRows.push({});
          }
          if (currentRows.length > 0 && currentHeaders[headerIndex]) {
            currentRows[currentRows.length - 1][currentHeaders[headerIndex]] = text;
          }

          return <td className="px-3 py-2 text-foreground/90">{children}</td>;
        },

        // Blockquote
        blockquote: ({ children }: { children?: React.ReactNode }) => (
          <blockquote className="my-2 pl-3 border-l-2 border-primary/40 text-foreground/70 italic">
            {children}
          </blockquote>
        ),

        // Horizontal rule
        hr: () => <hr className="my-3 border-primary/20" />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
